import { Select as AntSelect } from 'antd';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowDropDown } from 'assets/icons';

import type { ISelectItem, ISelectProps, ITagRenderProps } from './Select.types';

/**
 * Unified Select component based on Ant Design.
 *
 * Supports single/multi select modes, search/filter, custom rendering,
 * and is compatible with Formik and other form libraries via controlled component pattern.
 *
 * @template V - The type of items, must have an `id` property
 *
 * @example Single select
 * ```tsx
 * <Select
 *   items={accounts}
 *   itemToString={a => a.name}
 *   value={selectedAccount}
 *   onChange={setSelectedAccount}
 *   placeholder="Select account"
 * />
 * ```
 *
 * @example Multi select with search
 * ```tsx
 * <Select
 *   mode="multiple"
 *   showSearch
 *   items={options}
 *   itemToString={o => o.label}
 *   value={selectedOptions}
 *   onChange={setSelectedOptions}
 * />
 * ```
 *
 * @example With Formik
 * ```tsx
 * <Select
 *   items={accounts}
 *   itemToString={a => a.name}
 *   value={formik.values.account}
 *   onChange={val => formik.setFieldValue('account', val)}
 *   errorMessage={formik.touched.account ? formik.errors.account : undefined}
 * />
 * ```
 */
export function Select<V extends ISelectItem>({
  // Core
  items,
  itemToString,

  // Value
  value,
  onChange,

  // Mode
  mode = 'single',

  // Search
  showSearch = false,
  filterOption = true,
  onSearch,

  // UI
  label,
  placeholder,
  disabled = false,
  loading = false,
  allowClear = false,

  // Customization
  suffixIcon,
  tagRender,
  optionRender,
  notFoundContent,

  // Dropdown
  popupMatchSelectWidth = false,
  maxTagCount = 1,
  maxTagTextLength,

  // Validation
  errorMessage,
  comment,

  // Accessibility
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
}: ISelectProps<V>): JSX.Element {
  // ─────────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────────

  const [isOpen, setIsOpen] = useState(false);

  // Close dropdown when disabled
  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Value Conversion
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Convert external value to internal string format for Ant Select.
   */
  const internalValue = useMemo(() => {
    if (value === null || value === undefined) {
      return mode === 'single' ? undefined : [];
    }

    if (Array.isArray(value)) {
      return value.map(item => item.id.toString());
    }

    return value.id.toString();
  }, [value, mode]);

  /**
   * Convert items to Ant Select options format.
   */
  const options = useMemo(
    () =>
      items.map(item => ({
        value: item.id.toString(),
        label: itemToString(item),
        // Store original item for retrieval
        _item: item,
      })),
    [items, itemToString],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Handle selection change and convert back to original item type.
   */
  const handleChange = useCallback(
    (selectedValue: string | string[] | undefined) => {
      if (!onChange) return;

      // Handle clear
      if (
        selectedValue === undefined ||
        (Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        onChange(mode === 'single' ? null : []);
        return;
      }

      // Handle single select
      if (typeof selectedValue === 'string') {
        const selectedItem = items.find(item => item.id.toString() === selectedValue);
        onChange(selectedItem ?? null);
        return;
      }

      // Handle multi select
      const selectedItems = selectedValue
        .map(val => items.find(item => item.id.toString() === val))
        .filter((item): item is V => item !== undefined);

      onChange(selectedItems);
    },
    [items, mode, onChange],
  );

  /**
   * Handle dropdown open/close state.
   */
  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  /**
   * Handle filter for search functionality.
   */
  const handleFilterOption = useCallback(
    (input: string, option: { value: string; label: string; _item: V } | undefined): boolean => {
      if (!option) return false;

      if (typeof filterOption === 'function') {
        return filterOption(input, option._item);
      }

      if (filterOption === false) {
        return true;
      }

      // Default filter: case-insensitive label match
      return option.label.toLowerCase().includes(input.toLowerCase());
    },
    [filterOption],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Custom Renders
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Default suffix icon with rotation animation.
   */
  const defaultSuffixIcon = (
    <button
      type="button"
      className={cn('flex items-center justify-center transition-transform duration-300', {
        'rotate-180': isOpen,
      })}
      tabIndex={-1}
    >
      <ArrowDropDown />
    </button>
  );

  /**
   * Default option render.
   * Ant Design passes: { data: { value, label, _item }, label, value, key }
   */
  const defaultOptionRender = (option: {
    data?: { label?: React.ReactNode; _item?: V };
    label?: React.ReactNode;
  }): JSX.Element => {
    // Access _item from the data property (Ant Design's option structure)
    const item = option.data?._item;
    if (optionRender && item) {
      return <>{optionRender(item)}</>;
    }
    return <div className="whitespace-nowrap">{option.label}</div>;
  };

  /**
   * Wrapper for custom tagRender to match Ant Design types.
   */
  const handleTagRender = useCallback(
    (props: {
      label: React.ReactNode;
      value: string;
      closable: boolean;
      onClose: () => void;
    }): React.ReactElement => {
      const tagProps: ITagRenderProps = {
        label: props.label,
        value: props.value,
        closable: props.closable,
        onClose: props.onClose,
      };

      if (tagRender) {
        const result = tagRender(tagProps);
        // Ensure we return a valid React element
        return <>{result}</>;
      }

      // Default tag render
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 mr-1 text-xs bg-zinc-700 text-zinc-200 rounded max-w-[150px]"
          onMouseDown={e => e.stopPropagation()}
        >
          <span className="truncate">{props.label}</span>
          {props.closable && (
            <span
              onClick={props.onClose}
              onKeyDown={e => e.key === 'Enter' && props.onClose()}
              role="button"
              tabIndex={0}
              className="cursor-pointer text-zinc-400 hover:text-white"
              aria-label="Remove"
            >
              x
            </span>
          )}
        </span>
      );
    },
    [tagRender],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  // Determine Ant Select mode
  const antMode = mode === 'single' ? undefined : mode === 'tags' ? 'tags' : 'multiple';

  return (
    <div className="flex flex-col gap-1" data-testid={dataTestId}>
      {label && <label className="text-xs font-medium text-zinc-300">{label}</label>}

      <AntSelect
        // Mode
        mode={antMode}
        // Value
        value={internalValue}
        onChange={handleChange}
        // Options
        options={options}
        // Search
        showSearch={showSearch}
        filterOption={showSearch ? handleFilterOption : false}
        onSearch={onSearch}
        // State
        disabled={disabled}
        loading={loading}
        allowClear={allowClear}
        open={isOpen}
        onOpenChange={handleOpenChange}
        // UI
        placeholder={placeholder}
        popupMatchSelectWidth={popupMatchSelectWidth}
        maxTagCount={maxTagCount}
        maxTagTextLength={maxTagTextLength}
        notFoundContent={
          notFoundContent ?? (
            <div className="text-zinc-400 text-center text-xs py-2">Not found...</div>
          )
        }
        // Custom renders
        suffixIcon={suffixIcon ?? defaultSuffixIcon}
        tagRender={handleTagRender}
        optionRender={defaultOptionRender}
        // Accessibility
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        // Styling
        className={cn(
          'w-full min-w-[100px]',
          '[&_.ant-select-selector]:rounded-lg',
          '[&_.ant-select-selector]:bg-zinc-800',
          '[&_.ant-select-selector]:border-zinc-600',
          '[&_.ant-select-selector]:text-zinc-200',
          '[&_.ant-select-selector]:text-xs',
          '[&_.ant-select-selector]:min-h-[32px]',
          '[&_.ant-select-selector:hover]:border-primary',
          '[&_.ant-select-selector:focus]:border-primary',
          '[&_.ant-select-selection-placeholder]:text-zinc-500',
          { '[&_.ant-select-selector]:border-red-500': !!errorMessage },
        )}
        classNames={{
          popup: {
            root: cn(
              'bg-zinc-800 rounded-lg',
              '[&_.ant-select-item-option]:text-zinc-300',
              '[&_.ant-select-item-option]:text-xs',
              '[&_.ant-select-item-option]:px-2',
              '[&_.ant-select-item-option]:py-1.5',
              '[&_.ant-select-item-option-active]:bg-zinc-700',
              '[&_.ant-select-item-option-active]:text-white',
              '[&_.ant-select-item-option-selected]:bg-zinc-600',
              '[&_.ant-select-item-option-selected]:text-white',
              '[&_.ant-select-item-option-selected_.ant-select-item-option-state]:hidden',
            ),
          },
        }}
      />

      {comment && !errorMessage && <span className="text-[10px] text-zinc-500">{comment}</span>}

      {errorMessage && <span className="text-[10px] text-red-500">{errorMessage}</span>}
    </div>
  );
}
